/* eslint-disable max-lines */
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import EditIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Edit.js';
import { LensTheme, StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Container,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { OnChangeValue } from 'react-select';
import { ReportSubstantiation } from 'src/api';
import { CoiStatusColorMap } from 'src/components/atlas/organisms/helpers/CoiStatusColorMap';
import { SpeakUpStatusColorMap } from 'src/components/atlas/organisms/helpers/SpeakUpColorMap';
import { DefaultOption, handleErrorToast } from 'src/components/molecules';
import { InviteToCaseTeamDrawer } from 'src/components/molecules/InviteToCaseDrawer/InviteToCaseTeamDrawer';
import { ReviewCaseTeamMemberDrawer } from 'src/components/molecules/InviteToCaseDrawer/ReviewCaseTeamMemberDrawer';
import { ReassignCaseModal } from 'src/components/organismsLegacy/CaseScreenHeader/components';
import { onMutateCaseStatus } from 'src/components/organismsLegacy/CreateReportHeader/useCaseStatus';
import { caseLeadUpdated } from 'src/components/pages/Case/actions/caseLeadUpdated';
import {
  reportDetailsVm,
  updateSubstantiationStatus,
} from 'src/components/pages/Case/Case.slice';
import {
  CategoryDisplayType,
  Maybe,
  ReportStatus,
  UserType,
  useReassignCaseLeadMutation,
  useReassignReportAssigneeMutation,
  useUpdateCaseStatusMutation,
} from 'src/graphql';
import { AppDispatch, TRootState } from 'src/store';
import theme from 'src/theme/theme';
import { CaseTypeEnum, Option, SubmissionModeForDisplay } from 'src/types';
import { capitaliseFirstLetter, reportStatusToLabel } from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import { caseTeamPanel } from '../Case/CaseTeamPanel/CaseTeamPanel.vm';
import { useUpdateReportDetails } from '../ReportDetailsContainer/hooks/useUpdateReportDetails';

type OverviewRow =
  | {
      key: string;
      type: 'text' | 'status' | 'substantiationStatus';
      testId: string;
      value: string;
    }
  | { key: string; type: 'caseLead'; testId: string; value: string | null }
  | {
      key: string;
      type: 'group';
      testId: string;
      value: { name: string; id: number }[];
    };

const caseStatuses = [
  ReportStatus.Read,
  ReportStatus.Investigating,
  ReportStatus.Closed,
];

export const CaseOverviewPanel = ({
  label,
  caseLead,
  categories,
  status,
  submittedAt,
  submissionRoute,
  lastUpdated,
  caseType,
  view = 'full',
}: {
  label?: string;
  caseLead?: Maybe<UserType>;
  categories?: CategoryDisplayType[];
  status?: string;
  submittedAt?: Date;
  submissionRoute?: string | null;
  lastUpdated?: Date | null;
  caseType: CaseTypeEnum;
  view?: 'limited' | 'full';
}) => {
  const { tokens, presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { caseTeamListItems, canEditCaseLead, availableCaseLeads, caseId } =
    useSelector(caseTeamPanel);
  const { canInviteToCase } = useSelector(caseTeamPanel);
  const reportId = useSelector(
    (state: TRootState) => state.case.caseById?.Reports?.[0]?.id,
  );

  const [showCaseLeadDropdown, setShowCaseLeadDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSubStatusDropdown, setShowSubStatusDropdown] = useState(false);
  const [selectedCaseLead, setSelectedCaseLead] = useState<
    Option | undefined
  >();
  const [isReassigning, setIsReassigning] = useState(false);
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [isCaseTeamMemberDrawerOpen, setIsCaseTeamMemberDrawerOpen] =
    useState(false);
  const [teamMemberIdToEdit, setTeamMemberIdToEdit] = useState<number | null>(
    null,
  );

  const showSourceRoutes = [
    SubmissionModeForDisplay.MANUAL_INTAKE,
    `${SubmissionModeForDisplay.MANUAL_INTAKE} - Anonymous`,
    SubmissionModeForDisplay.BULK_IMPORT,
  ];
  const vm = useSelector(reportDetailsVm({ reportId: reportId ?? '' }));
  const renderMiReportInput =
    vm.miReportSource !== '' && showSourceRoutes.includes(vm.submissionRoute);

  const { mutationUpdateSubstantiationStatus } = useUpdateReportDetails();

  const { mutateAsync: mutateReassignCaseLead } = useReassignCaseLeadMutation();
  const { mutateAsync: mutateReassignReportAssignee } =
    useReassignReportAssigneeMutation();
  const updateCaseStatus = useUpdateCaseStatusMutation({
    onError: (error) => {
      console.error('CaseOverviewPanel: failed to update case status', {
        caseId,
        error,
      });
      handleErrorToast({ toastMessage: 'Failed to update case status' });
    },
    onMutate: onMutateCaseStatus({ id: caseId, queryClient }),
    onSettled: () =>
      queryClient.invalidateQueries(['CaseById', { id: caseId }]),
  });

  const caseLeadTitle =
    caseType === CaseTypeEnum.COI ? 'Reviewer' : 'Case lead';
  const caseTeamTitle =
    caseType === CaseTypeEnum.COI ? 'Reviewer group' : 'Case team';

  const handleReassignConfirm = async () => {
    if (!selectedCaseLead || isReassigning) return;
    setIsReassigning(true);
    try {
      await dispatch(
        caseLeadUpdated({
          caseId,
          caseLeadId: Number(selectedCaseLead.value),
          mutateReassignCaseLead,
          queryClient,
        }),
      ).unwrap();

      if (reportId) {
        await mutateReassignReportAssignee({
          newReportAssigneeId: Number(selectedCaseLead.value),
          report: reportId,
          caseId,
        });
        queryClient.invalidateQueries(['CaseById']);
      }

      setSelectedCaseLead(undefined);
      setShowCaseLeadDropdown(false);
    } catch (e) {
      handleErrorToast({
        toastMessage: `Failed to reassign ${caseLeadTitle.toLowerCase()}`,
      });
    } finally {
      setIsReassigning(false);
    }
  };

  const handleReassignCancel = () => {
    setSelectedCaseLead(undefined);
    setShowCaseLeadDropdown(false);
  };

  const handleEditTeamMember = (memberId: number) => {
    setTeamMemberIdToEdit(memberId);
    setIsCaseTeamMemberDrawerOpen(true);
  };

  const handleCaseStatusOnChange = (
    option: OnChangeValue<DefaultOption, boolean>,
  ) => {
    // TODO: check if needs to blocked by country and region fields
    if (caseId && option && 'value' in option && option.value) {
      const value = option.value as string;
      updateCaseStatus.mutate({
        newCaseStatus: value as ReportStatus,
        caseId,
      });
      setShowStatusDropdown(false);
    }
  };

  const leadName = caseLead?.displayName ?? caseLead?.name ?? null;
  const teamGroup =
    caseTeamListItems
      ?.map((m) => ({ name: m?.name, id: m?.id }))
      .filter((m) => Boolean(m?.name))
      .filter((m) => Boolean(m?.id)) ?? [];

  const rows: OverviewRow[] = [
    {
      key: 'Form title',
      type: 'text',
      testId: 'form-title',
      value: label ?? 'N/A',
    },
    {
      key: 'Category',
      type: 'text',
      testId: 'category',
      value: categories?.map((c) => c.name).join(', ') || 'N/A',
    },
    {
      key: 'Submission date',
      type: 'text',
      testId: 'submission-date',
      value: submittedAt
        ? formatDate(submittedAt, DateFormats.DD_MM_YYYY)
        : 'N/A',
    },
    caseType === CaseTypeEnum.SPEAK_UP && {
      key: 'Submission type',
      type: 'text',
      testId: 'submission-route',
      value: submissionRoute ?? 'N/A',
    },
    caseType === CaseTypeEnum.SPEAK_UP &&
      renderMiReportInput && {
        key: 'Source',
        type: 'text',
        testId: 'report-source',
        value: vm.miReportSource,
      },
    view === 'full'
      ? {
          key: caseLeadTitle,
          type: 'caseLead',
          testId: 'case-lead',
          value: leadName,
        }
      : null,
    view === 'full'
      ? {
          key: caseTeamTitle,
          type: 'group',
          testId: 'case-team',
          value: teamGroup,
        }
      : null,
    { key: 'Status', type: 'status', testId: 'status', value: status ?? 'N/A' },
    caseType === CaseTypeEnum.SPEAK_UP && {
      key: 'Substantiation status',
      type: 'substantiationStatus',
      testId: 'substantiation-status',
      value: vm.substantiationStatus.label ?? 'N/A',
    },
    view === 'full'
      ? {
          key: 'Last updated',
          type: 'text',
          testId: 'last-updated',
          value: lastUpdated
            ? formatDate(lastUpdated, DateFormats.DD_MM_YYYY)
            : 'N/A',
        }
      : null,
  ].filter(Boolean) as OverviewRow[];

  const CaseLeadDisplay = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      {leadName ? (
        <>
          <Avatar
            {...getAvatarProps({ size: 'medium', color: 'red' })}
            aria-label={leadName}
            role="img"
          >
            {getInitials(leadName)}
          </Avatar>
          {leadName}
        </>
      ) : (
        'N/A'
      )}
      {canEditCaseLead && (
        <IconButton
          size="small"
          onClick={() => setShowCaseLeadDropdown(true)}
          data-testid="edit-lead-button"
          aria-label={`Edit ${caseLeadTitle.toLocaleLowerCase()}`}
          sx={{ marginLeft: 'auto' }}
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );

  const CaseLeadAssignment = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <Box sx={{ minWidth: 200 }}>
        <Autocomplete
          options={availableCaseLeads}
          getOptionLabel={(option) => String(option.label)}
          onChange={(_event, option) => {
            if (option) {
              setSelectedCaseLead(option as Option);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Search ${caseLeadTitle.toLocaleLowerCase()}...`}
              size="small"
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  'data-testid': 'lead-search-dropdown',
                },
              }}
            />
          )}
        />
      </Box>
      <IconButton
        size="small"
        onClick={() => setShowCaseLeadDropdown(false)}
        data-testid="cancel-edit-case-lead-button"
        aria-label="Cancel editing lead"
        sx={{ marginLeft: 'auto' }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  const StatusChange = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <FormControl sx={{ width: '100%' }}>
        <Select
          value={status ?? ''}
          onChange={(e) =>
            handleCaseStatusOnChange({
              value: e.target.value,
              label: reportStatusToLabel(e.target.value as ReportStatus),
            })
          }
        >
          {caseStatuses.map((s) => (
            <MenuItem key={s} value={s}>
              {reportStatusToLabel(s)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        onClick={() => setShowStatusDropdown(false)}
        data-testid="cancel-edit-status-button"
        aria-label="Cancel editing status"
        sx={{ marginLeft: 'auto' }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  const SubstantiationStatusChange = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <FormControl sx={{ width: '100%' }}>
        <Select
          value={vm.substantiationStatus.label ?? ''}
          onChange={(option) => {
            if (!reportId) return;
            dispatch(
              updateSubstantiationStatus({
                mutationUpdateSubstantiationStatus,
                report: reportId,
                newSubstantiationStatus: option.target.value as string,
                caseId: vm.caseId,
              }),
            );
            setShowSubStatusDropdown(false);
          }}
        >
          {Object.values(ReportSubstantiation).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        onClick={() => setShowSubStatusDropdown(false)}
        data-testid="cancel-edit-substantiation-status-button"
        aria-label="Cancel editing substantiation status"
        sx={{ marginLeft: 'auto' }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  const renderValue = (row: OverviewRow) => {
    switch (row.type) {
      case 'status':
        if (showStatusDropdown) {
          return StatusChange;
        } else {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <StatusIndicator
                customColor={
                  caseType === CaseTypeEnum.COI
                    ? CoiStatusColorMap(row.value)
                    : SpeakUpStatusColorMap(row.value)
                }
                label={capitaliseFirstLetter(row.value)}
                aria-label={`Case status - ${row.value}`}
              />
              {caseType === CaseTypeEnum.SPEAK_UP && (
                <IconButton
                  size="small"
                  onClick={() => setShowStatusDropdown(true)}
                  data-testid="edit-status-button"
                  aria-label="Edit status"
                  sx={{ marginLeft: 'auto' }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          );
        }
      case 'substantiationStatus':
        if (showSubStatusDropdown) {
          return SubstantiationStatusChange;
        } else {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {row.value}
              {caseType === CaseTypeEnum.SPEAK_UP && (
                <IconButton
                  size="small"
                  onClick={() => setShowSubStatusDropdown(true)}
                  data-testid="edit-substantiation-status-button"
                  aria-label="Edit substantiation status"
                  sx={{ marginLeft: 'auto' }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          );
        }
      case 'caseLead':
        if (showCaseLeadDropdown) {
          return CaseLeadAssignment;
        }
        return CaseLeadDisplay;
      case 'group':
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {row.value.length === 0 && <span>N/A</span>}
            {row.value.length > 0 && (
              <AvatarGroup
                max={6}
                sx={{ display: 'flex', flexDirection: 'row' }}
                slotProps={{
                  surplus: { sx: { ...getAvatarProps({ size: 'medium' }).sx } },
                }}
              >
                {(row.value as { name: string; id: number }[]).map((member) => (
                  <Tooltip key={member.id} title={member.name}>
                    <Avatar
                      sx={{
                        ...getAvatarProps({ size: 'medium' }).sx,
                        cursor: canInviteToCase ? 'pointer' : 'default',
                      }}
                      role="button"
                      data-testid={`edit-team-member-${member.id}`}
                      onClick={() =>
                        canInviteToCase
                          ? handleEditTeamMember(member.id)
                          : undefined
                      }
                    >
                      {getInitials(member.name)}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
            {canInviteToCase && (
              <IconButton
                size="small"
                onClick={() => setIsInviteDrawerOpen(true)}
                data-testid="add-members-button"
                aria-label="Add members"
                sx={{ marginLeft: 'auto' }}
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
        );
      default:
        return row.value;
    }
  };

  const pageType = caseType === CaseTypeEnum.COI ? 'Disclosure' : 'Case';

  return (
    <Container
      sx={{
        backgroundColor: theme.atlas.semantic.color.surface.default,
        borderRadius: tokens.semantic.radius.md.value,
        padding: tokens.core.spacing[2].value,
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: 600 }}>
        {pageType} overview
      </div>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.key}</TableCell>
                <TableCell
                  data-testid={row.testId}
                  sx={{ '&:focus-within': { boxShadow: 'none' } }}
                >
                  {renderValue(row)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ReassignCaseModal
        open={selectedCaseLead !== undefined}
        setOpen={() => setSelectedCaseLead(undefined)}
        onConfirm={handleReassignConfirm}
        onCancel={handleReassignCancel}
      />
      <InviteToCaseTeamDrawer
        caseType={caseType}
        open={isInviteDrawerOpen}
        onClose={() => setIsInviteDrawerOpen(false)}
      />
      <ReviewCaseTeamMemberDrawer
        caseType={caseType}
        caseId={caseId}
        userId={teamMemberIdToEdit}
        open={isCaseTeamMemberDrawerOpen}
        onClose={() => setIsCaseTeamMemberDrawerOpen(false)}
      />
    </Container>
  );
};
